<template>
  <div class="basic-site-information">
    <div class="basic-site-information__header">
      <h2>Basic Site Information</h2>
    </div>
    
    <themed-form
      :fields="siteFields"
      v-model="siteData"
      :errors="errors"
      :loading="loading"
      layout="grid"
      :columns="2"
      @submit="handleSubmit"
    >
      <template #submit="{ loading }">
        <div class="basic-site-information__actions">
          <button
            type="submit"
            class="basic-site-information__button basic-site-information__button--primary"
            :disabled="loading"
            aria-label="Save site information"
          >
            <span v-if="!loading">Save Site</span>
            <span v-else>Saving...</span>
          </button>
        </div>
      </template>
    </themed-form>
  </div>
</template>

<script>
export default {
  name: 'BasicSiteInformation',
  
  props: {
    site: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      siteData: {
        subsidiary_id: this.site.subsidiary_id || null,
        lease_or_own: this.site.lease_or_own || 'Leased'
      },
      errors: {},
      loading: false,
      subsidiaries: []
    };
  },
  
  computed: {
    siteFields() {
      return [
        {
          name: 'subsidiary_id',
          type: 'select',
          label: 'Subsidiary',
          options: this.subsidiaries,
          required: true,
          col: 12,
          placeholder: 'Select a subsidiary'
        },
        {
          name: 'lease_or_own',
          type: 'select',
          label: 'Leased or Owned',
          options: [
            { value: 'Leased', label: 'Leased' },
            { value: 'Owned', label: 'Owned' }
          ],
          required: true,
          col: 12
        }
      ];
    },
    
    subsidiary() {
      if (!this.siteData.subsidiary_id || !this.site.client?.subsidiaries) {
        return null;
      }
      return this.site.client.subsidiaries.find(
        sub => sub.id === this.siteData.subsidiary_id
      );
    }
  },
  
  watch: {
    site: {
      handler(newSite) {
        if (newSite) {
          this.siteData.subsidiary_id = newSite.subsidiary_id;
          this.siteData.lease_or_own = newSite.lease_or_own || 'Leased';
        }
      },
      deep: true
    }
  },
  
  mounted() {
    this.loadSubsidiaries();
  },
  
  methods: {
    async loadSubsidiaries() {
      try {
        // If subsidiaries are already in the site data, use them
        if (this.site.client?.subsidiaries) {
          this.subsidiaries = this.site.client.subsidiaries.map(sub => ({
            value: sub.id,
            label: sub.name
          }));
        } else {
          // Otherwise fetch from API
          const response = await axios.get('/api/subsidiaries');
          this.subsidiaries = response.data.map(sub => ({
            value: sub.id,
            label: sub.name
          }));
        }
      } catch (error) {
        console.error('Error loading subsidiaries:', error);
        if (this.$toast) {
          this.$toast.error('Failed to load subsidiaries');
        }
      }
    },
    
    async handleSubmit() {
      this.loading = true;
      this.errors = {};
      
      try {
        // Prepare the data for submission
        const updateData = {
          ...this.siteData,
          _method: 'PATCH'
        };
        
        const response = await axios.post(`/sites/${this.site.id}`, updateData);
        
        if (this.$toast) {
          this.$toast.success('Site updated successfully');
        }
        
        // Emit event to parent component
        this.$emit('site-updated', response.data.site);
      } catch (error) {
        // Handle validation errors
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
        
        // Display error message
        const message = error.response?.data?.message || 'Failed to update site';
        if (this.$toast) {
          this.$toast.error(message);
        } else {
          console.error(message, error);
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
